import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Crown, Calendar, Mail, Shield, Clock, Lock, CheckCircle, Phone } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VIP_INFO = {
  diavelico: { name: 'Diavélico', discount: '50%', color: 'text-diavel-red', border: 'border-diavel-red/30', bg: 'bg-red-900/10' },
  silver: { name: 'Diavélico Silver', discount: '55%', color: 'text-zinc-300', border: 'border-zinc-400/30', bg: 'bg-zinc-800/30' },
  gold: { name: 'Diavélico Gold', discount: '60%', color: 'text-yellow-400', border: 'border-yellow-500/30', bg: 'bg-yellow-900/10' },
  diamond: { name: 'Diavélico Diamond', discount: '65%', color: 'text-cyan-300', border: 'border-cyan-400/30', bg: 'bg-cyan-900/10' },
};

const ALL_LEVELS = [
  { key: 'diavelico', min: '0€', max: '199€' },
  { key: 'silver', min: '200€', max: '499€' },
  { key: 'gold', min: '500€', max: '999€' },
  { key: 'diamond', min: '1.000€', max: 'Sin limite' },
];

export default function ProfilePage() {
  const { user } = useAuth();
  const vip = VIP_INFO[user?.vip_level] || VIP_INFO.diavelico;
  const [membership, setMembership] = useState(null);
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '' });
  const [pwMsg, setPwMsg] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/membership/status`, { withCredentials: true });
        setMembership(res.data);
      } catch {}
    })();
  }, []);

  const handleChangePassword = async () => {
    setPwMsg(null);
    if (pwForm.new_password.length < 6) { setPwMsg({ type: 'error', text: 'Minimo 6 caracteres' }); return; }
    try {
      await axios.post(`${API}/auth/change-password`, pwForm, { withCredentials: true });
      setPwMsg({ type: 'success', text: 'Contraseña actualizada' });
      setPwForm({ current_password: '', new_password: '' });
    } catch (err) {
      setPwMsg({ type: 'error', text: err.response?.data?.detail || 'Error' });
    }
  };

  return (
    <div className="p-6 md:p-10 pb-24 md:pb-10 max-w-5xl" data-testid="profile-page">
      <div className="mb-10 animate-fade-in-up">
        <h1 className="font-heading font-extrabold text-3xl tracking-tight uppercase text-white">Mi Perfil</h1>
        <p className="text-zinc-500 mt-2">Tu informacion de miembro</p>
      </div>

      {/* User info */}
      <div className="bg-diavel-card border border-white/5 p-8 mb-8 animate-fade-in-up animate-delay-100">
        <div className="flex items-center gap-5 mb-8">
          <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center text-xl font-bold text-white overflow-hidden">
            {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : user?.name?.[0]?.toUpperCase()}
          </div>
          <div>
            <h2 className="font-heading font-bold text-xl text-white" data-testid="profile-name">{user?.name}</h2>
            <div className={`inline-flex items-center gap-2 px-3 py-1 mt-2 ${vip.bg} ${vip.border} border`}>
              <Crown className={`w-3 h-3 ${vip.color}`} />
              <span className={`font-mono text-xs tracking-wider uppercase ${vip.color}`}>{vip.name}</span>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Mail className="w-4 h-4 text-zinc-600" />
            <div>
              <p className="font-mono text-xs tracking-wider uppercase text-zinc-500">Email</p>
              <p className="text-sm text-white">{user?.email}</p>
            </div>
          </div>
          {user?.phone && (
            <div className="flex items-center gap-3">
              <Phone className="w-4 h-4 text-zinc-600" />
              <div>
                <p className="font-mono text-xs tracking-wider uppercase text-zinc-500">Telefono</p>
                <p className="text-sm text-white">{user.phone}</p>
              </div>
            </div>
          )}
          <div className="flex items-center gap-3">
            <Calendar className="w-4 h-4 text-zinc-600" />
            <div>
              <p className="font-mono text-xs tracking-wider uppercase text-zinc-500">Miembro desde</p>
              <p className="text-sm text-white">{user?.created_at ? new Date(user.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' }) : '—'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Shield className="w-4 h-4 text-zinc-600" />
            <div>
              <p className="font-mono text-xs tracking-wider uppercase text-zinc-500">Descuento activo</p>
              <p className="text-sm text-white font-bold">{vip.discount} en tatuajes por sesiones</p>
            </div>
          </div>
        </div>
      </div>

      {/* Membership status */}
      {membership && (
        <div className="bg-diavel-card border border-white/5 p-8 mb-8 animate-fade-in-up animate-delay-200">
          <h2 className="font-heading font-bold text-lg uppercase tracking-wider text-white mb-6">Mi Membresia</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="font-mono text-xs tracking-wider uppercase text-zinc-500 mb-1">Estado</p>
              <p className={`text-lg font-bold uppercase ${membership.status === 'active' ? 'text-green-400' : membership.status === 'expired' ? 'text-red-400' : 'text-yellow-400'}`}>
                {membership.status === 'active' ? 'Activa' : membership.status === 'expired' ? 'Expirada' : 'Pendiente'}
              </p>
            </div>
            <div>
              <p className="font-mono text-xs tracking-wider uppercase text-zinc-500 mb-1">Precio anual</p>
              <p className="text-lg font-bold text-white">{membership.membership_price}€/año</p>
            </div>
            <div>
              <p className="font-mono text-xs tracking-wider uppercase text-zinc-500 mb-1">Expira</p>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-zinc-500" />
                <p className="text-lg font-bold text-white">
                  {membership.membership_expiry 
                    ? new Date(membership.membership_expiry).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })
                    : 'Sin activar'}
                </p>
              </div>
              {membership.days_remaining !== null && membership.status === 'active' && (
                <p className={`text-xs mt-1 ${membership.days_remaining <= 30 ? 'text-yellow-400' : 'text-zinc-500'}`}>
                  {membership.days_remaining} dias restantes
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Change password */}
      <div className="bg-diavel-card border border-white/5 p-8 mb-8 animate-fade-in-up animate-delay-300">
        <h2 className="font-heading font-bold text-lg uppercase tracking-wider text-white mb-6 flex items-center gap-2">
          <Lock className="w-5 h-5" /> Cambiar Contraseña
        </h2>
        <div className="space-y-4 max-w-md">
          <div>
            <label className="font-mono text-xs tracking-wider uppercase text-zinc-500 block mb-2">Contraseña actual</label>
            <input data-testid="profile-current-pw" type="password" value={pwForm.current_password}
              onChange={e => setPwForm(p => ({ ...p, current_password: e.target.value }))}
              className="w-full bg-zinc-900/50 border border-white/10 focus:border-red-600 h-12 px-4 text-white outline-none transition-all duration-300" />
          </div>
          <div>
            <label className="font-mono text-xs tracking-wider uppercase text-zinc-500 block mb-2">Nueva contraseña</label>
            <input data-testid="profile-new-pw" type="password" value={pwForm.new_password}
              onChange={e => setPwForm(p => ({ ...p, new_password: e.target.value }))}
              className="w-full bg-zinc-900/50 border border-white/10 focus:border-red-600 h-12 px-4 text-white outline-none transition-all duration-300" />
          </div>
          <button onClick={handleChangePassword} data-testid="profile-change-pw-btn"
            className="bg-diavel-red text-white font-bold uppercase tracking-wider py-3 px-8 hover:bg-red-700 transition-all duration-300 text-sm">
            Cambiar contraseña
          </button>
          {pwMsg && (
            <div className={`flex items-center gap-2 ${pwMsg.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
              {pwMsg.type === 'success' && <CheckCircle className="w-4 h-4" />}
              <p className="text-sm">{pwMsg.text}</p>
            </div>
          )}
        </div>
      </div>

      {/* VIP Levels */}
      <div className="bg-diavel-card border border-white/5 p-8 animate-fade-in-up animate-delay-400">
        <h2 className="font-heading font-bold text-lg uppercase tracking-wider text-white mb-6">Niveles VIP</h2>
        <div className="space-y-4">
          {ALL_LEVELS.map(level => {
            const info = VIP_INFO[level.key];
            const isCurrent = user?.vip_level === level.key;
            return (
              <div key={level.key} className={`p-4 border transition-all duration-300 ${isCurrent ? `${info.border} ${info.bg}` : 'border-white/5'}`}>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <Crown className={`w-4 h-4 ${isCurrent ? info.color : 'text-zinc-600'}`} />
                    <span className={`font-heading font-bold text-sm uppercase tracking-wider ${isCurrent ? info.color : 'text-zinc-400'}`}>{info.name}</span>
                    {isCurrent && <span className="font-mono text-[10px] bg-diavel-red text-white px-2 py-0.5 tracking-wider">ACTUAL</span>}
                  </div>
                  <span className={`font-heading font-bold text-sm ${isCurrent ? info.color : 'text-zinc-500'}`}>{info.discount}</span>
                </div>
                <p className="font-mono text-xs text-zinc-600 mt-2 ml-7">Gasto total: {level.min} — {level.max}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
